// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const WARNING_BEFORE_MS = 2 * 60 * 1000;   // warn 2 min before logout
const ACTIVITY_THROTTLE_MS = 30 * 1000;    // only reset timer at most every 30s

export const AuthProvider = ({ children }) => {
  const [staff, setStaff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(120);

  const logoutTimer  = useRef(null);
  const warningTimer = useRef(null);
  const countdown    = useRef(null);
  const lastReset    = useRef(0);
  const doLogoutRef  = useRef(null);
  const resetIdleRef = useRef(null);

  // Restore session from localStorage ONCE
  useEffect(() => {
    const token = localStorage.getItem('numa_token');
    const storedStaff = localStorage.getItem('numa_staff');
    if (token && storedStaff) {
      try {
        const parsed = JSON.parse(storedStaff);
        setStaff(parsed);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      } catch {
        localStorage.removeItem('numa_token');
        localStorage.removeItem('numa_staff');
      }
    }
    setLoading(false);
  }, []);

  const clearTimers = () => {
    if (logoutTimer.current)  clearTimeout(logoutTimer.current);
    if (warningTimer.current) clearTimeout(warningTimer.current);
    if (countdown.current)    clearInterval(countdown.current);
    logoutTimer.current = warningTimer.current = countdown.current = null;
  };

  // Logout function lives in a ref so handlers always see the latest
  doLogoutRef.current = async () => {
    clearTimers();
    setShowSessionWarning(false);
    try { await api.post('/auth/logout'); } catch {}
    localStorage.removeItem('numa_token');
    localStorage.removeItem('numa_staff');
    delete api.defaults.headers.common['Authorization'];
    setStaff(null);
  };

  // Reset idle function lives in a ref so we can call it without changing listener deps
  resetIdleRef.current = () => {
    if (!localStorage.getItem('numa_token')) return;
    clearTimers();
    setShowSessionWarning(false);

    warningTimer.current = setTimeout(() => {
      setShowSessionWarning(true);
      setSecondsLeft(120);
      countdown.current = setInterval(() => {
        setSecondsLeft(s => {
          if (s <= 1) { clearInterval(countdown.current); return 0; }
          return s - 1;
        });
      }, 1000);
    }, SESSION_TIMEOUT_MS - WARNING_BEFORE_MS);

    logoutTimer.current = setTimeout(() => {
      doLogoutRef.current && doLogoutRef.current();
    }, SESSION_TIMEOUT_MS);
  };

  // Set up activity listeners exactly once per login
  useEffect(() => {
    if (!staff) { clearTimers(); setShowSessionWarning(false); return; }

    const events = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll', 'click', 'wheel', 'numa_activity'];
    // THROTTLED handler — only resets the timer if 30s has passed since last activity
    const handler = () => {
      const now = Date.now();
      if (now - lastReset.current > ACTIVITY_THROTTLE_MS) {
        lastReset.current = now;
        resetIdleRef.current && resetIdleRef.current();
      }
    };

    events.forEach(e => window.addEventListener(e, handler, { passive: true }));
    // Initial timer start
    lastReset.current = Date.now();
    resetIdleRef.current && resetIdleRef.current();

    return () => {
      events.forEach(e => window.removeEventListener(e, handler));
      clearTimers();
    };
  }, [staff]);

  const login = useCallback((token, staffData) => {
    localStorage.setItem('numa_token', token);
    localStorage.setItem('numa_staff', JSON.stringify(staffData));
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setStaff(staffData);
  }, []);

  const logout = useCallback(async () => {
    await doLogoutRef.current();
  }, []);

  const stayLoggedIn = useCallback(() => {
    setShowSessionWarning(false);
    lastReset.current = Date.now();
    resetIdleRef.current && resetIdleRef.current();
  }, []);

  const isAdmin = staff?.role === 'admin' || (staff?.permissionLevel || 0) >= 10;
  const isSupervisor = staff?.role === 'supervisor' || (staff?.permissionLevel || 0) >= 3 || isAdmin;
  const isNurse = staff?.role === 'nurse' || (staff?.permissionLevel || 0) >= 2 || isSupervisor;

  return (
    <AuthContext.Provider value={{
      staff, login, logout, loading,
      isAdmin, isSupervisor, isNurse,
      showSessionWarning, secondsLeft, stayLoggedIn
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
